// User Profile Types for Match Calculation

export interface UserProfile {
  // Basic Info
  firstName: string;
  lastName: string;
  email: string;
  
  // Academic
  gpa: number | null; // 0.0 - 4.0, null = not yet set
  gradeLevel: 'middle-school' | 'freshman' | 'sophomore' | 'junior' | 'senior' | 'hs-graduate' | 'graduate'
    | 'middle-school-6th' | 'middle-school-7th' | 'middle-school-8th'
    | 'high-school-freshman' | 'high-school-sophomore' | 'high-school-junior' | 'high-school-senior'
    | 'college-freshman' | 'college-sophomore' | 'college-junior' | 'college-senior';
  intendedMajor: string;
  majorCategory: MajorCategory;
  
  // Demographics
  state: string;
  ethnicity: Ethnicity[];
  gender: 'male' | 'female' | 'non-binary' | 'prefer-not-to-say';
  
  // Financial
  financialNeed: FinancialNeedLevel;
  pellGrantEligible: boolean;
  fafsaComplete: boolean;
  householdIncome: IncomeRange;
  
  // Background
  firstGeneration: boolean;
  citizenship: 'us-citizen' | 'permanent-resident' | 'daca' | 'international' | 'undocumented';
  
  // Extracurriculars (checkboxes)
  extracurriculars: ExtracurricularCategory[];
  
  // Interests
  careerInterests: string[];
  
  // Personalization
  avatar?: string; // Preset avatar ID from AvatarBank (e.g., 'basketball', 'rocket')

  // Profile completion
  isComplete: boolean;
  completedAt?: string;

  // Onboarding completion — persisted to profiles.onboarding_completed
  // Survives browser/device changes unlike the localStorage flag
  onboardingCompleted: boolean;

  // Account role — set during signup, persisted to profiles.account_role
  accountRole?: 'student' | 'parent';
  // If this student account was created by a parent
  parentUserId?: string;
}

export type MajorCategory = 
  | 'stem'
  | 'business'
  | 'humanities'
  | 'arts'
  | 'social-sciences'
  | 'health'
  | 'education'
  | 'law'
  | 'engineering'
  | 'music'
  | 'journalism'
  | 'agriculture'
  | 'undecided';

export type Ethnicity = 
  | 'african-american'
  | 'hispanic-latino'
  | 'asian'
  | 'pacific-islander'
  | 'native-american'
  | 'white'
  | 'middle-eastern'
  | 'multi-racial'
  | 'other';

export type FinancialNeedLevel = 
  | 'high'      // Qualifies for need-based aid
  | 'moderate'  // Some financial need
  | 'low'       // Minimal need
  | 'none';     // No financial need

export type IncomeRange = 
  | 'under-30k'
  | '30k-60k'
  | '60k-100k'
  | '100k-150k'
  | 'over-150k';

export type ExtracurricularCategory = 
  | 'athletics'
  | 'arts-music'
  | 'community-service'
  | 'leadership'
  | 'stem-clubs'
  | 'debate-speech'
  | 'journalism'
  | 'religious'
  | 'cultural'
  | 'entrepreneurship'
  | 'environmental'
  | 'military-rotc';

// Default empty profile
export const defaultProfile: UserProfile = {
  firstName: '',
  lastName: '',
  email: '',
  gpa: null,
  gradeLevel: 'senior',
  intendedMajor: '',
  majorCategory: 'undecided',
  state: '',
  ethnicity: [],
  gender: 'prefer-not-to-say',
  financialNeed: 'moderate',
  pellGrantEligible: false,
  fafsaComplete: false,
  householdIncome: '60k-100k',
  firstGeneration: false,
  citizenship: 'us-citizen',
  extracurriculars: [],
  careerInterests: [],
  isComplete: false,
  onboardingCompleted: false,
};

// Labels for display
export const ethnicityLabels: Record<Ethnicity, string> = {
  'african-american': 'Black / African American',
  'hispanic-latino': 'Hispanic / Latino',
  'asian': 'Asian',
  'pacific-islander': 'Pacific Islander',
  'native-american': 'Native American / Alaska Native',
  'white': 'White / Caucasian',
  'middle-eastern': 'Middle Eastern / North African',
  'multi-racial': 'Multi-Racial',
  'other': 'Other',
};

export const majorCategoryLabels: Record<MajorCategory, string> = {
  'stem': 'STEM (Science, Technology, Engineering, Math)',
  'business': 'Business / Finance / Economics',
  'humanities': 'Humanities (English, History, Philosophy)',
  'arts': 'Arts (Visual, Performing, Design)',
  'social-sciences': 'Social Sciences (Psychology, Sociology, Political Science)',
  'health': 'Health / Pre-Med / Nursing',
  'education': 'Education / Teaching',
  'law': 'Pre-Law / Criminal Justice',
  'engineering': 'Engineering',
  'music': 'Music',
  'journalism': 'Journalism / Communications',
  'agriculture': 'Agriculture / Environmental Science',
  'undecided': 'Undecided',
};

export const extracurricularLabels: Record<ExtracurricularCategory, string> = {
  'athletics': 'Athletics / Sports',
  'arts-music': 'Arts / Music / Theater',
  'community-service': 'Community Service / Volunteering',
  'leadership': 'Student Government / Leadership',
  'stem-clubs': 'STEM Clubs / Robotics / Coding',
  'debate-speech': 'Debate / Speech / Model UN',
  'journalism': 'Journalism / School Paper / Yearbook',
  'religious': 'Religious Organizations',
  'cultural': 'Cultural / Heritage Clubs',
  'entrepreneurship': 'Entrepreneurship / Business Clubs',
  'environmental': 'Environmental / Sustainability',
  'military-rotc': 'Military / JROTC / ROTC',
};

export const stateOptions = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'District of Columbia', 'Puerto Rico'
];
